/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import DecoupledEditorBase from '@ckeditor/ckeditor5-editor-decoupled/src/decouplededitor';

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor';
import FontBackgroundColor from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor';
import UploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder';
import EasyImage from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock';
import Link from '@ckeditor/ckeditor5-link/src/link';
import List from '@ckeditor/ckeditor5-list/src/list';
import ListStyle from '@ckeditor/ckeditor5-list/src/liststyle';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation';
import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices';
import Video from '@ckeditor/ckeditor5-video-upload/src/video';
import VideoToolbar from '@ckeditor/ckeditor5-video-upload/src/videotoolbar';
import VideoResize from '@ckeditor/ckeditor5-video-upload/src/videoresize';
import VideoStyle from '@ckeditor/ckeditor5-video-upload/src/videostyle';
import VideoUpload from '@ckeditor/ckeditor5-video-upload/src/videoupload';
import IndentFirst from '@ckeditor/ckeditor5-toolbox/src/indent-first';

export default class DecoupledEditor extends DecoupledEditorBase {}

// Plugins to include in the build.
DecoupledEditor.builtinPlugins = [
	Essentials,
	Alignment,
	FontSize,
	FontFamily,
	FontColor,
	FontBackgroundColor,
	UploadAdapter,
	Autoformat,
	Bold,
	Italic,
	Strikethrough,
	Underline,
	BlockQuote,
	CKFinder,
	CloudServices,
	EasyImage,
	Heading,
	Image,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	ImageResize,
	Indent,
	IndentBlock,
	Link,
	List,
	ListStyle,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	Table,
	TableToolbar,
	TextTransformation,
	Video,
	VideoToolbar,
	VideoUpload,
	VideoResize,
	VideoStyle,
	IndentFirst,
];

// Editor configuration.
DecoupledEditor.defaultConfig = {
	video: {
		upload: {
			types: ['mp4', 'quicktime'],
			allowMultipleFiles: false,
		},
		styles: ['alignLeft', 'alignCenter', 'alignRight'],
		resizeOptions: [
			{
				name: 'videoResize:original',
				label: 'Original',
				icon: 'original',
			},
			{
				name: 'videoResize:50',
				label: '50',
				icon: 'medium',
			},
			{
				name: 'videoResize:75',
				label: '75',
				icon: 'large',
			},
		],
		toolbar: [
			'videoStyle:alignLeft',
			'videoStyle:alignCenter',
			'videoStyle:alignRight',
			// '|',
			// 'videoResize:50',
			// 'videoResize:75',
			// 'videoResize:original',
		],
	},
	fontFamily: {
		options: ['default', '微软雅黑, Microsoft Yahei'],
	},
	fontSize: {
		options: [12, 15, 'default', 20, 25, 30, 32, 40, 50, 60],
	},
	toolbar: {
		items: [
			'heading',
			'|',
			'fontfamily',
			'fontsize',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'bold',
			'italic',
			'underline',
			'strikethrough',
			'|',
			'alignment',
			'|',
			'numberedList',
			'bulletedList',
			'|',
			'outdent',
			'indent',
			'indentFirst',
			'|',
			// 'link',
			'blockquote',
			'uploadImage',
			'insertTable',
			// 'mediaEmbed',
			'videoUpload',
			'|',
			'undo',
			'redo',
		],
	},
	image: {
		styles: ['alignLeft', 'alignCenter', 'alignRight'],
		resizeUnit: 'px',
		// resizeOptions: [
		// 	{
		// 		name: 'resizeImage:100%',
		// 		label: 'Original',
		// 		value: null,
		// 	},
		// 	{
		// 		name: 'resizeImage:50',
		// 		label: '50%',
		// 		value: '50',
		// 	},
		// 	{
		// 		name: 'resizeImage:75',
		// 		label: '75%',
		// 		value: '75',
		// 	},
		// ],
		toolbar: [
			'imageStyle:alignLeft',
			'imageStyle:alignCenter',
			'imageStyle:alignRight',
			// '|',
			// 'resizeImage',
			// '|',
			// 'imageTextAlternative'
		],
	},
	table: {
		contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
	},
	// This value must be kept in sync with the language defined in webpack.config.js.
	language: 'en',
};
